import { useState } from 'react';
import { useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { Plus } from 'lucide-react';
import type { FunctionComponent } from 'react';
import type { LucideIcon } from 'lucide-react';
import { OnboardingLayout, useSlideSetup } from '@/features/client-onboarding';
import { selectClient } from '@/model';
import { usePolicyDetails } from '@/features/genworth/policy-details/use-policy-details';
import { PolicyDetailsDialog } from '@/features/genworth/policy-details/policy-details-dialog';
import type { GenworthPolicyDetails } from '@/features/genworth/policy-details/policy-details-storage';
import { Button } from '@design-system';

export interface PolicyStat {
  label: string;
  value?: string;
  caption?: string;
}

export interface PolicyEducationConfig {
  eyebrow: string;
  title: string;
  icon: LucideIcon;
  paragraphs: string[];
  nextButtonLabel?: string;
  stats?: (details: GenworthPolicyDetails) => PolicyStat[];
}

/**
 * Template for the Genworth policy-education slides. Slides that carry a figure render a
 * split panel — explanation on the left, the client's actual numbers on the right — while
 * concept-only slides (the intro) stay centered. Each slide passes its own component as
 * `slidePage` so it registers its own slide identity.
 */
export function PolicyEducationSlide({
  slidePage,
  clientId,
  config,
}: {
  slidePage: FunctionComponent<ClientIdProps>;
  clientId: string;
  config: PolicyEducationConfig;
}) {
  const { details } = usePolicyDetails(clientId);
  const client = useSelector(selectClient);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

  useSlideSetup({
    slidePage,
    nextButtonLabel: config.nextButtonLabel ?? 'Continue',
  });

  const stats = config.stats?.(details) ?? [];

  return (
    <OnboardingLayout>
      <div className="mx-auto flex min-h-full w-full max-w-5xl flex-col items-center justify-center py-10 md:py-16">
        {stats.length > 0 ? (
          <SplitPanel
            config={config}
            stats={stats}
            onAddDetails={() => setDetailsDialogOpen(true)}
          />
        ) : (
          <CenteredPanel config={config} />
        )}
      </div>
      {client && (
        <PolicyDetailsDialog
          client={client}
          open={detailsDialogOpen}
          onClose={() => setDetailsDialogOpen(false)}
        />
      )}
    </OnboardingLayout>
  );
}

function SplitPanel({
  config,
  stats,
  onAddDetails,
}: {
  config: PolicyEducationConfig;
  stats: PolicyStat[];
  onAddDetails: () => void;
}) {
  const Icon = config.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45 }}
      className="bg-background grid w-full overflow-hidden rounded-2xl border md:grid-cols-[1.5fr_1fr]"
    >
      <div className="flex flex-col gap-4 p-4 text-left md:p-8">
        <div className="bg-primary/10 text-primary flex size-14 items-center justify-center rounded-lg">
          <Icon className="size-7" />
        </div>
        <p className="text-primary text-xs font-semibold uppercase">{config.eyebrow}</p>
        <h1 className="text-balance text-2xl font-bold text-gray-950 md:text-3xl">
          {config.title}
        </h1>
        <div className="flex flex-col gap-2">
          {config.paragraphs.map((paragraph, index) => (
            <p
              key={index}
              className="text-foreground text-pretty text-base leading-relaxed"
            >
              {paragraph}
            </p>
          ))}
        </div>
      </div>

      <div className="grid h-full p-1">
        <div className="bg-primary/10 relative flex flex-col justify-end gap-6 overflow-hidden rounded-xl p-4 md:p-8">
          <p className="text-primary-dark/80 relative text-xs font-semibold uppercase">
            Your Policy
          </p>
          <div className="relative flex flex-col gap-6">
            {stats.map((stat, index) => (
              <StatBlock
                key={index}
                stat={stat}
                index={index}
                onAddDetails={onAddDetails}
              />
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function StatBlock({
  stat,
  index,
  onAddDetails,
}: {
  stat: PolicyStat;
  index: number;
  onAddDetails: () => void;
}) {
  const hasValue = stat.value !== undefined && stat.value !== '';

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.15 + index * 0.08 }}
      className="flex flex-col gap-1"
    >
      <p className="text-primary-dark/70 text-sm font-medium">{stat.label}</p>
      {hasValue ? (
        <p className="text-primary-dark text-4xl font-bold leading-tight tracking-tight md:text-5xl">
          {stat.value}
        </p>
      ) : (
        <Button
          type="button"
          variant="outline"
          onClick={onAddDetails}
          className="text-primary border-primary/50 hover:border-primary group border-dashed"
        >
          <Plus />
          Add policy details
        </Button>
      )}
      {hasValue && stat.caption && <p className="text-primary-dark/70 text-sm">{stat.caption}</p>}
    </motion.div>
  );
}

function CenteredPanel({ config }: { config: PolicyEducationConfig }) {
  const Icon = config.icon;

  return (
    <div className="flex max-w-2xl flex-col items-center gap-5 text-center">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4 }}
        className="bg-primary/10 text-primary flex size-16 items-center justify-center rounded-2xl md:size-20"
      >
        <Icon className="size-8 md:size-10" />
      </motion.div>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className="text-primary text-sm font-semibold uppercase tracking-wide"
      >
        {config.eyebrow}
      </motion.p>

      <motion.h1
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.15 }}
        className="text-balance text-2xl font-bold text-gray-950 md:text-4xl"
      >
        {config.title}
      </motion.h1>

      <div className="flex flex-col gap-4">
        {config.paragraphs.map((paragraph, index) => (
          <motion.p
            key={index}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 + index * 0.08 }}
            className="text-foreground text-balance text-base leading-relaxed md:text-lg"
          >
            {paragraph}
          </motion.p>
        ))}
      </div>
    </div>
  );
}
